import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";

const Dashboard = () => {
    const [documents, setDocuments] = useState([]);
    const [orders, setOrders] = useState([]);
    const [summary, setSummary] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [file, setFile] = useState(null);
    const [type, setType] = useState("medical");
    const [orderId, setOrderId] = useState("");
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("dashboard");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    const refreshAll = async () => {
        setBusy(true);
        setError("");
        try {
            const userRes = await api.get("/me");
            setCurrentUser(userRes.data);

            await Promise.all([
                api.get("/documents").then((res) => setDocuments(res.data)),
                api.get(`/user/${userRes.data.id}/orders`).then((res) => setOrders(res.data)),
                api.get(`/user/${userRes.data.id}/dashboard-summary`).then((res) => setSummary(res.data)),
            ]);
        } catch (err) {
            setError("Unable to load dashboard data. Please retry.");
            console.error("Failed to load dashboard data", err);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        refreshAll();
    }, []);

    const uploadDocument = async (e) => {
        e.preventDefault();
        if (!file) return;
        setLoading(true);
        setError("");

        try {
            const formData = new FormData();
            formData.append("document", file);
            formData.append("type", type);
            if (orderId) formData.append("order_id", orderId);

            await api.post("/documents", formData);
            setFile(null);
            setOrderId("");
            setIsModalOpen(false); // close modal after upload
            await refreshAll();
        } catch (err) {
            setError("Upload failed. Please try again.");
            console.error("Upload failed", err);
        } finally {
            setLoading(false);
        }
    };

    const viewDocument = async (id) => {
        const res = await api.get(`/documents/${id}/view`, { responseType: "blob" });
        const fileURL = URL.createObjectURL(res.data);
        window.open(fileURL, "_blank");
    };

    const downloadDocument = async (doc) => {
        const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type }));
        const link = document.createElement("a");
        const filename = `${doc.type}-${doc.id}.${doc.path.split(".").pop()}`;
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const documentStats = useMemo(() => {
        if (!documents.length) return { total: 0, types: 0, linked: 0, lastUploaded: null };
        const types = new Set(documents.map((d) => d.type)).size;
        const linked = documents.filter((d) => d.order_id).length;
        const lastUploaded = documents[0]?.created_at || null;
        return { total: documents.length, types, linked, lastUploaded };
    }, [documents]);

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <aside className="w-64 bg-white shadow-md border-r hidden md:block">
                <div className="p-6">
                    <h2 className="text-xl font-semibold mb-6">Dashboard</h2>
                    <nav className="space-y-2">
                        <button
                            onClick={() => setActiveTab("dashboard")}
                            className={`block w-full text-left px-4 py-2 rounded-lg ${activeTab === "dashboard" ? "bg-[#56C7D5] text-white" : "hover:bg-gray-100"
                                }`}
                        >
                            Dashboard
                        </button>
                        <button
                            onClick={() => setActiveTab("orders")}
                            className={`block w-full text-left px-4 py-2 rounded-lg ${activeTab === "orders" ? "bg-[#56C7D5] text-white" : "hover:bg-gray-100"
                                }`}
                        >
                            Orders
                        </button>
                        <button
                            onClick={() => setActiveTab("documents")}
                            className={`block w-full text-left px-4 py-2 rounded-lg ${activeTab === "documents" ? "bg-[#56C7D5] text-white" : "hover:bg-gray-100"
                                }`}
                        >
                            Documents
                        </button>
                    </nav>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-semibold">Welcome{currentUser?.name ? `, ${currentUser.name}` : ""}</h1>
                        <p className="text-sm text-gray-500">Live view of your uploads and orders.</p>
                    </div>
                </div>

                {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

                {activeTab === "dashboard" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center justify-center">
                            <h3 className="text-lg font-semibold mb-2">Total Documents</h3>
                            <p className="text-3xl font-extrabold">{documents.length}</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center justify-center">
                            <h3 className="text-lg font-semibold mb-2">Total Orders</h3>
                            <p className="text-3xl font-extrabold">{orders.length}</p>
                        </div>
                    </div>
                )}

                {activeTab === "orders" && (
                    <div className="bg-white rounded-xl shadow-sm border">
                        <h2 className="text-xl font-semibold mb-4 px-6 pt-4">Orders</h2>
                        <table className="min-w-full divide-y">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-semibold">Order ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold">Medicine</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold">Total Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold">Shipping</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold">Created</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {orders.map((order) => (
                                    <tr key={order.id}>
                                        <td className="px-6 py-4">{order.id}</td>
                                        <td className="px-6 py-4">{order.medicine_name || "—"}</td>
                                        <td className="px-6 py-4 capitalize">{order.status}</td>
                                        <td className="px-6 py-4">{order.total_amount}</td>
                                        <td className="px-6 py-4 max-w-xs truncate">{order.shipping_address || "—"}</td>
                                        <td className="px-6 py-4">{order.created_at ? new Date(order.created_at).toLocaleString() : "—"}</td>
                                    </tr>
                                ))}
                                {!orders.length && (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-sm text-gray-500">
                                            No orders yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === "documents" && (
                    <>
                        {/* Add Document Button */}
                        <div className="mb-4">
                            <button
                                onClick={() => {
                                    setIsModalOpen(true);
                                    if (orders.length) setOrderId(orders[0].id); // Preselect first order
                                }}
                                className="bg-[#56C7D5] text-white px-4 py-2 rounded-lg"
                            >
                                Add Document
                            </button>
                        </div>

                        {/* Modal */}
                        {isModalOpen && (
                            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
                                <div className="bg-white rounded-lg w-full max-w-md md:max-w-lg p-6 relative overflow-y-auto max-h-full">
                                    {/* Close button */}
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold"
                                    >
                                        &times;
                                    </button>

                                    <h2 className="text-xl font-semibold mb-6 text-center md:text-left">Upload Document</h2>

                                    <form onSubmit={uploadDocument} className="flex flex-col gap-4">
                                        {/* Select Document */}
                                        <div className="w-full">
                                            <label className="block text-sm font-medium mb-1">Select Document</label>
                                            <input
                                                type="file"
                                                required
                                                onChange={(e) => setFile(e.target.files[0])}
                                                className="block w-full text-sm
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-lg file:border-0
                            file:bg-[#56C7D5] file:text-white"
                                            />
                                        </div>

                                        {/* Document Type */}
                                        <div className="w-full">
                                            <label className="block text-sm font-medium mb-1">Document Type</label>
                                            <select
                                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                                value={type}
                                                onChange={(e) => setType(e.target.value)}
                                            >
                                                <option value="medical">Medical</option>
                                                <option value="prescription">Prescription</option>
                                                <option value="id">ID</option>
                                            </select>
                                        </div>

                                        {/* Link to Order */}
                                        <div className="w-full">
                                            <label className="block text-sm font-medium mb-1">Link to Order</label>
                                            <select
                                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                                value={orderId}
                                                onChange={(e) => setOrderId(e.target.value)}
                                                required
                                            >
                                                {orders.map((order) => (
                                                    <option key={order.id} value={order.id}>
                                                        #{order.id} - {order.status}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Submit Button */}
                                        <button
                                            disabled={loading}
                                            className="w-full bg-[#56C7D5] text-white py-2 rounded-lg"
                                        >
                                            {loading ? "Uploading..." : "Upload"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}



                        {/* Documents Table */}
                        <div className="bg-white rounded-xl shadow-sm border">
                            <table className="min-w-full divide-y">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold">Order</th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold">Size</th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold">Actions</th>
                                    </tr>
                                </thead>

                                <tbody className="divide-y">
                                    {documents.filter((doc) => doc.path).map((doc) => (
                                        <tr key={doc.id}>
                                            <td className="px-6 py-4 capitalize">{doc.type}</td>
                                            <td className="px-6 py-4 capitalize">{doc.status}</td>
                                            <td className="px-6 py-4">{doc.order_id ? `#${doc.order_id}` : "—"}</td>
                                            <td className="px-6 py-4">{doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : "—"}</td>
                                            <td className="px-6 py-4 text-right space-x-4">
                                                <button onClick={() => viewDocument(doc.id)} className="text-blue-600 hover:underline">
                                                    View
                                                </button>
                                                <button onClick={() => downloadDocument(doc)} className="text-green-600 hover:underline">
                                                    Download
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {!documents.length && (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-sm text-gray-500">
                                                No documents yet. Upload your first file to get started.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default Dashboard;
